import React from 'react';
import PropTypes from 'prop-types';
import withData from '@bd/bdcore-data';
import { throttle } from 'lodash';
import MoveTo from 'moveto';
import { css, cx } from '@bd/bdui-styles/emotion';
import { colors } from '../styles';

const transitionTime = 500;

const styles = css`
  .AccordionToggle_scrollRef {
    position: absolute;
    width: 100%;
    height: 1px;
    @media (min-width: 768px) {
      margin-top: -40px;
    }
    @media (min-width: 992px) {
      margin-top: -60px;
    }
  }
  .AccordionToggle_accordionRef {
    @media (min-width: 768px) {
      display: none;
    }
  }
  .AccordionToggle_button {
    display: block;
    padding: 0 20px;
    cursor: pointer;
  }
  .AccordionToggle_buttonText {
    display: block;
    padding-top: 26px;
    padding-bottom: 30px;
    border-bottom: 1px solid #EBEBEB;
    font-size: 16px;
    font-weight: bold;
    letter-spacing: 0.08125em;
    line-height: 1.375;
    text-transform: uppercase;
    position: relative;
    &::before, &::after {
      content: '';
      display: block;
      position: absolute;
      top: 50%;
      right: 10px;
      width: 12px;
      height: 2px;
      background-color: ${colors.color1};
      transition: transform ${transitionTime}ms ease;
    }
  }
  .AccordionToggle_button-closed {
    .AccordionToggle_buttonText {
      &::before {
        transform: translateY(-50%) rotateZ(0deg);
      }
      &::after {
        transform: translateY(-50%) rotateZ(-90deg);
      }
    }
  }
  .AccordionToggle_button-open {
    .AccordionToggle_buttonText {
      &::before {
        transform: translateY(-50%) rotateZ(-180deg);
      }
      &::after {
        transform: translateY(-50%) rotateZ(-180deg);
      }
    }
  }
  .AccordionToggle_contentWrapper {
    height: 0;
    overflow: hidden;
    transition: height ${transitionTime}ms ease;
    @media (min-width: 768px) {
      height: auto;
      overflow: visible;
      transition: none;
    }
  }
`;


function scrollToTop(ref) {
  setTimeout(() => {
    const moveTo = new MoveTo({
      tolerance: 100,
    });
    moveTo.move(ref);
  }, 100);
}

function elemIsVisible(elem) {
  return elem && !!(elem.offsetWidth || elem.offsetHeight || elem.getClientRects().length);
}


class AccordionToggle extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      showContent: false,
      allowToggle: false,
    };

    this.handleResize = throttle(this.handleResize.bind(this), 100);
  }

  componentDidMount() {
    setTimeout(() => {
      const { router: { location }, openOnId, isOpen } = this.props;
      const active = location.hash.substr(1) === openOnId.replace('#', '');
      const isMobile = !window.matchMedia('(min-width: 992px)').matches;

      if ((active && isMobile) || isOpen) {
        this.changeVisibleState(true);
      }

      this.checkAllowToggle();
    }, 500);

    window.addEventListener('resize', this.handleResize);
  }

  componentWillReceiveProps(nextProps) {
    setTimeout(() => {
      const { router: { location }, openOnId } = nextProps;
      const active = location.hash.substr(1) === openOnId.replace('#', '');
      const isMobile = !window.matchMedia('(min-width: 992px)').matches;

      if (active && isMobile) {
        this.changeVisibleState(true);
      }
    }, 100);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  setControlRef = ref => {
    this.controlRef = ref;
  }

  setSizeRef = ref => {
    this.sizeRef = ref;
  }

  setContainerRef = ref => {
    this.containerRef = ref;
  }

  setContainerHeight = showContent => {
    if (showContent) {
      // const element = this.sizeRef;
      // let height = element.getBoundingClientRect().height;
      // const style = window.getComputedStyle(element);

      // height: element height + vertical padding & borders
      // now we just need to add vertical margins
      // height = ['top', 'bottom']
      //   .map(side => {
      //     return parseInt(style[`margin-${side}`], 10);
      //   })
      //   .reduce((total, side) => {
      //     return total + side;
      //   }, height);

      this.containerRef.style.height = 'auto';
    } else {
      this.containerRef.style.height = null;
    }
  }

  changeVisibleState = showContent => {
    this.setState({
      showContent,
    }, this.setContainerHeight(showContent));
  };

  checkAllowToggle = () => {
    this.setState({
      allowToggle: elemIsVisible(this.controlRef),
    });
  }

  handleResize = () => {
    this.checkAllowToggle();
    this.setContainerHeight(this.state.showContent && this.state.allowToggle);
  }

  toggleContent = e => {
    const { showContent } = this.state;

    if (!elemIsVisible(this.controlRef) || (e.which && ![32, 13].includes(e.which))) {
      return;
    }

    this.changeVisibleState(!showContent);
    if (this.props.scollToTop) {
      scrollToTop(this.controlRef);
    }
  }

  render() {
    const { button, children, className, id } = this.props;
    const { allowToggle, showContent } = this.state;

    return (
      <div className={cx(styles, className)}>
        <div className="AccordionToggle_scrollRef" id={id} />
        <div className="AccordionToggle_accordionRef" ref={ref => this.setControlRef(ref)} />
        {
          allowToggle ?
          (
            <span
              className={[
                'AccordionToggle_button',
                showContent ? 'AccordionToggle_button-open' : 'AccordionToggle_button-closed',
              ].join(' ')}
              role="button"
              tabIndex="0"
              onClick={this.toggleContent}
              onKeyUp={this.toggleContent}
            >
              <span className="AccordionToggle_buttonText GoldText">{button}</span>
            </span>
          ) :
          null
        }
        <div className="AccordionToggle_contentWrapper" ref={ref => this.setContainerRef(ref)}>
          <div className="AccordionToggle_content" ref={ref => this.setSizeRef(ref)}>
            {children}
          </div>
        </div>
      </div>
    );
  }
}

AccordionToggle.propTypes = {
  button: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  id: PropTypes.string,
  isOpen: PropTypes.bool,
  openOnId: PropTypes.string,
  router: PropTypes.object.isRequired,
  scollToTop: PropTypes.bool,
};

AccordionToggle.defaultProps = {
  className: undefined,
  isOpen: undefined,
  id: undefined,
  openOnId: undefined,
  scollToTop: true,
};

export default withData({
  router: true,
})(AccordionToggle);



// WEBPACK FOOTER //
// ./src/components/AccordionToggle.js